import { Injectable, LOCALE_ID, computed, inject, signal } from '@angular/core';
import { formatNumber } from '@angular/common';
import { Observable, of } from 'rxjs';
import { catchError, tap, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/product.models';
import {
  CurrencyDto,
  CurrencySettingsDto,
  ExchangeRateDto,
  SetExchangeRateRequest,
  UpdateTenantCurrenciesRequest,
} from '../models/currency.models';
import { ApiService } from './api.service';

/**
 * Ko'p valyutalilikning frontenddagi YAGONA manbai: metama'lumot, formatlash, kurslar.
 *
 * Loyihada pul faqat shu servis (yoki uning `money`/`moneyValue` pipe'lari) orqali
 * ko'rsatiladi. `toFixed()`, `toLocaleString()` yoki `| number: '1.0-0'` bilan pul
 * formatlash taqiqlanadi — aks holda yangi valyuta qo'shilganda 168 ta joyni qo'lda
 * tuzatish kerak bo'ladi.
 */
/**
 * Ilova ishga tushishida sozlama shuncha kutiladi. Undan keyin zaxira qiymat bilan
 * davom etiladi — server javob bermagani uchun panel umuman ochilmay qolmasin.
 */
const LOAD_TIMEOUT_MS = 5000;

@Injectable({ providedIn: 'root' })
export class CurrencyService extends ApiService {
  private readonly endpoint = `${environment.apiUrl}/api/Currencies`;
  private readonly locale = inject(LOCALE_ID);

  private readonly _settings = signal<CurrencySettingsDto | null>(null);
  readonly settings = this._settings.asReadonly();

  /**
   * Server javobi kelgunicha ishlatiladigan zaxira. Bu valyutalar RO'YXATI emas —
   * bitta qiymat: ko'p valyutagacha butun tizim shu valyutada edi, shuning uchun
   * birinchi render avvalgidek chiqadi va ekran "sakramaydi".
   */
  private static readonly BOOTSTRAP: CurrencyDto = {
    code: 'UZS',
    name: "So'm",
    symbol: "so'm",
    decimals: 0,
    roundingStep: 100,
    symbolPosition: 'After',
    sortOrder: 1,
    isActive: true,
    isQuoteCurrency: true,
  };

  /** Valyuta ko'rsatilmaganda ishlatiladigan kod (tenant kotirovka valyutasi). */
  readonly defaultCode = computed(
    () => this._settings()?.quoteCurrency ?? CurrencyService.BOOTSTRAP.code,
  );

  /** Yangi savdoda tanlash mumkin bo'lgan valyutalar. */
  readonly activeCurrencies = computed(() =>
    (this._settings()?.currencies ?? [CurrencyService.BOOTSTRAP]).filter((c) => c.isActive),
  );

  /**
   * `p-select` uchun tayyor variantlar: `USD — Dollar`.
   *
   * Yagona joyda turadi, chunki tanlagich o'nga yaqin ekranda takrorlanadi —
   * yorliq formatini har birida qayta yozish ularni vaqt o'tib bir-biridan
   * uzoqlashtiradi.
   */
  readonly currencyOptions = computed(() =>
    this.activeCurrencies().map((c) => ({ label: `${c.code} — ${c.name}`, value: c.code })),
  );

  /** Bir nechta valyuta yoqilganmi — tanlagichni ko'rsatish sharti. */
  readonly hasMultipleCurrencies = computed(() => this.activeCurrencies().length > 1);

  readonly currentRates = computed<ExchangeRateDto[]>(() => this._settings()?.currentRates ?? []);

  /** Kurs eskirgan bo'lsa panelda ogohlantirish ko'rsatish uchun. */
  readonly hasStaleRate = computed(() => this.currentRates().some((r) => r.isStale));

  // ── Metama'lumot ────────────────────────────────────────────────────────────

  /**
   * Valyuta ma'lumoti. Noma'lum kod uchun ham xavfsiz qiymat qaytaradi — eski
   * yozuvda yo'qolgan valyuta uchraganda ekran buzilmasin.
   */
  info(code?: string | null): CurrencyDto {
    const list = this._settings()?.currencies;
    const wanted = (code ?? this.defaultCode()).toUpperCase();
    const found = list?.find((c) => c.code === wanted);
    if (found) return found;
    if (wanted === CurrencyService.BOOTSTRAP.code) return CurrencyService.BOOTSTRAP;
    // Noma'lum: kodning o'zini belgi sifatida ko'rsatamiz (`100 TRY`).
    //
    // `roundingStep` ALBATTA qayta belgilanadi: `BOOTSTRAP` — so'm, uning qadami
    // 100. Uni meros qilib olsak, noma'lum valyutadagi `$0.99` `round()` dan
    // `$0` bo'lib chiqardi. Noma'lum valyuta uchun qadam yo'q (0) — yaxlitlash
    // faqat kasr xonasi bo'yicha bo'ladi.
    return {
      ...CurrencyService.BOOTSTRAP,
      code: wanted,
      name: wanted,
      symbol: wanted,
      decimals: 2,
      roundingStep: 0,
    };
  }

  /**
   * Narx KIRITISH maydonida ruxsat etilgan kasr xonalari soni.
   *
   * `decimals` — SUMMA uchun (UZS da 0, USD da 2), lekin dona narxi undan aniqroq
   * bo'lishi mumkin: `4.4234 $` yoki `12.5 so'm`. Shuning uchun kiritish bazadagi
   * narx aniqligiga tenglashtiriladi — forma bazadan ko'p ham, kam ham qabul
   * qilmaydi, ya'ni kiritilgan raqam jimgina yaxlitlanib qolmaydi.
   */
  priceInputDecimals(code?: string | null): number {
    const currency = this.info(code);
    return Math.max(currency.decimals, CurrencyService.PRICE_MAX_DECIMALS);
  }

  // ── Formatlash ──────────────────────────────────────────────────────────────

  /**
   * Faqat raqam, valyuta belgisisiz: `1,265,000`.
   *
   * `decimals` — eng KAM xona (USD da `4.00`, UZS da `1,265,000`), eng ko'pi emas.
   * Narx summadan aniqroq bo'lishi mumkin (`4.4234 $`, `12.5 so'm`), va u qiymat
   * bazada aynan shunday saqlanadi — yaxlitlab ko'rsatish foydalanuvchiga o'zi
   * kiritgan narxdan boshqa raqamni ko'rsatardi. Shuning uchun ortiqcha xonalar
   * BOR bo'lsa chiqariladi, yo'q bo'lsa qo'shimcha nol qo'shilmaydi.
   */
  formatValue(amount: number | null | undefined, code?: string | null): string {
    const currency = this.info(code);
    const value = amount ?? 0;
    const max = Math.max(currency.decimals, this.significantDecimals(value));
    return formatNumber(value, this.locale, `1.${currency.decimals}-${max}`);
  }

  /**
   * Sondagi HAQIQIY kasr xonalari soni (ortiqcha nollarsiz): `4.4234` → 4,
   * `4.20` → 1, `4` → 0. `PRICE_MAX_DECIMALS` bilan cheklanadi, aks holda
   * ikkilik kasr xatosi (`0.1 + 0.2`) o'nlab xona bo'lib chiqib ketardi.
   */
  private significantDecimals(value: number): number {
    if (!Number.isFinite(value) || Number.isInteger(value)) return 0;
    const fixed = Math.abs(value).toFixed(CurrencyService.PRICE_MAX_DECIMALS);
    const fraction = fixed.split('.')[1] ?? '';
    return fraction.replace(/0+$/, '').length;
  }

  /** Bazadagi narx aniqligi — `Products.SellPrice` = `decimal(28,9)`. */
  private static readonly PRICE_MAX_DECIMALS = 9;

  /** Raqam + valyuta belgisi: `1,265,000 so'm` yoki `$100.00`. */
  format(amount: number | null | undefined, code?: string | null): string {
    const currency = this.info(code);
    const text = this.formatValue(amount, code);
    return currency.symbolPosition === 'Before'
      ? `${currency.symbol}${text}`
      : `${text} ${currency.symbol}`;
  }

  /**
   * Raqam + valyuta belgisi, valyutaning STANDART kasr xonasida QOTIRILGAN —
   * `format()`dagi "ortiqcha xona bo'lsa chiqariladi" qoidasi YO'Q.
   *
   * `format()`/`formatValue()` NARX uchun mo'ljallangan (`4.4234 $` — foydalanuvchi
   * aynan shu raqamni kiritgan, yashirish noto'g'ri). Kassa/Moliya kabi ekranlarda
   * esa ko'rsatiladigan HAR BIR raqam — bir nechta operatsiyaning JAMI (summa),
   * dona narxi emas. Summada ortiqcha kasr xonasi foydali aniqlik emas — backend
   * hisob-kitobidagi (kurs, o'rtachalash) qoldiq shovqin, va uzun `0.123456789`
   * qatori ekranni band qilib, o'qishni qiyinlashtiradi. Shuning uchun bu yerda
   * hamisha valyutaning o'z kasr xonasi (UZS: 0, USD: 2) — na kam, na ko'p.
   */
  formatFixed(amount: number | null | undefined, code?: string | null): string {
    const currency = this.info(code);
    const value = amount ?? 0;
    const text = formatNumber(value, this.locale, `1.${currency.decimals}-${currency.decimals}`);
    return currency.symbolPosition === 'Before'
      ? `${currency.symbol}${text}`
      : `${text} ${currency.symbol}`;
  }

  /**
   * Summani valyutaning eng mayda amaliy birligiga yaxlitlaydi (backenddagi
   * `Money.Round` bilan bir xil mantiq).
   *
   * Bu faqat TAKLIF ko'rsatish uchun — yakuniy summani kassir belgilaydi.
   */
  round(amount: number, code?: string | null): number {
    const currency = this.info(code);
    if (currency.roundingStep <= 0) {
      const factor = Math.pow(10, currency.decimals);
      return Math.round(amount * factor) / factor;
    }
    const steps = Math.round(Math.abs(amount) / currency.roundingStep);
    const rounded = steps * currency.roundingStep;
    return amount < 0 ? -rounded : rounded;
  }

  /**
   * DONA NARXINI yaxlitlaydi — `round()` dan farqli, `roundingStep` ga tortmaydi.
   *
   * `round()` summani kassa qadamiga tortadi (UZS da 100 so'm), chunki mijoz
   * qo'liga pul beradi. Narx esa summa emas: `4.4234 $` yoki `0.02 $` lik tovar
   * bo'lishi mumkin va uni 100 ga tortish narxni butunlay yo'q qiladi.
   * Shuning uchun bu yerda faqat bazadagi aniqlikka (`decimal(28,9)`) keltiriladi.
   *
   * Ikkilik kasr xatosini ham shu yerda yig'ishtiradi: `0.1 + 0.2` →
   * `0.30000000000000004` emas, `0.3`.
   */
  roundPrice(amount: number, code?: string | null): number {
    if (!Number.isFinite(amount)) return 0;
    const decimals = this.priceInputDecimals(code);
    // `toFixed` + `Number` — `Math.round(x * 1e6) / 1e6` dan aniqroq: katta
    // qiymatlarda ko'paytirish o'zi kasr xatosi kiritadi.
    return Number(amount.toFixed(decimals));
  }

  /**
   * `from` → `to` kursi. Topilmasa `null` — chaqiruvchi kursni so'rashi kerak,
   * taxmin qilmasligi kerak.
   */
  rate(from: string, to: string): number | null {
    if (from === to) return 1;
    const direct = this.currentRates().find((r) => r.fromCurrency === from && r.toCurrency === to);
    return direct ? direct.rate : null;
  }

  /**
   * SHU juftlikning kursi eskirganmi.
   *
   * `hasStaleRate` dan farqi: u "biror kurs eskirgan" deydi, bu esa aynan
   * ishlatilayotgan juftlikni tekshiradi. To'lov ekranida umumiy bayroq
   * ishlatilsa, mutlaqo yangi kurs bilan to'layotgan foydalanuvchiga ham
   * "kurs eskirgan" deb qizil ogohlantirish chiqib turardi.
   */
  isRateStale(from: string, to: string): boolean {
    if (from === to) return false;
    const direct = this.currentRates().find((r) => r.fromCurrency === from && r.toCurrency === to);
    return direct?.isStale ?? false;
  }

  // ── API ─────────────────────────────────────────────────────────────────────

  /**
   * Sozlamalarni yuklaydi.
   *
   * Bu chaqiruv ilova ishga tushishida BLOKLAB kutiladi, shuning uchun u hech qanday
   * holatda osilib qolmasligi shart: xato ham, sekinlik ham zaxira qiymat bilan
   * davom etadi. Aks holda foydalanuvchi oq ekran ko'radi.
   */
  load(): Observable<CurrencySettingsDto | null> {
    return this.http.get<ApiResponse<CurrencySettingsDto>>(`${this.endpoint}/settings`).pipe(
      timeout(LOAD_TIMEOUT_MS),
      tap((res) => {
        if (res.success && res.data) this._settings.set(res.data);
      }),
      catchError(() => of(null)),
    ) as unknown as Observable<CurrencySettingsDto | null>;
  }

  getRateHistory(from?: string, to?: string, limit = 100) {
    const params: Record<string, string> = { limit: String(limit) };
    if (from) params['from'] = from;
    if (to) params['to'] = to;
    return this.http.get<ApiResponse<ExchangeRateDto[]>>(`${this.endpoint}/rates/history`, {
      params,
    });
  }

  setRate(request: SetExchangeRateRequest) {
    return this.http.post<ApiResponse<{ id: string }>>(`${this.endpoint}/rates`, request);
  }

  updateSettings(request: UpdateTenantCurrenciesRequest) {
    return this.http.put<void>(`${this.endpoint}/settings`, request);
  }
}
